import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { motion, MotionConfig } from "framer-motion";
import { ArrowDown, ArrowRight, ArrowUp, CalendarDays, Check, Lock, Pencil, Play, Plus, Radio, Search, Trash2, Upload, X, Youtube } from "lucide-react";
import { upcomingLives, YOUTUBE_LIVE_URL } from "@/lib/schedule";
import { DENVER, EASE, HEART, Head, Ink, VIEW, datePart } from "../bits";
import { Part } from "../Page";
import { JoinButton, Locked, M, MembersPage, VisBadge, useRole, useTo, withRole } from "./Members";
import { CLASSES, PLANS, PROJECTS, byNewest, can, classStart, classStatus, hasReplay, type ClassVideo, type Project } from "./sample";
import "../SectionsA.css";
import "./Classes.css";

/* The club's class library (/blue-2/members/classes): every live-class replay,
   grouped by project, newest first, with search, a skill filter and "continue
   watching". Classes that haven't aired yet (tonight's, say) sit on the shelf
   as "airs tonight · replay right after" cards, never as replays (see hasReplay in
   sample.ts). Visitors and Thread Friends see the titles but not the playback.
   Chelsea gets an "Upload replay" dialog (prototype: nothing is uploaded).

   Prototype state lives in a tiny module store below so the library and the
   class page agree on what's watched and on any replay Chelsea "uploads"
   during the session. It resets on reload. */

/* ─── Shared: time helpers ─── */

const pad = (n: number) => String(n).padStart(2, "0");
/** "m:ss" or "h:mm:ss" to seconds. */
export const toSec = (t: string) => t.trim().split(":").reduce((a, n) => a * 60 + Number(n), 0);
/** Seconds to "m:ss" / "h:mm:ss". */
export const clock = (s: number) => {
  const x = Math.max(0, Math.floor(s)), h = Math.floor(x / 3600), m = Math.floor((x % 3600) / 60), r = x % 60;
  return h ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
};
/** Seconds to "1 hr 32 min". */
export const human = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h} hr${m ? ` ${m} min` : ""}` : `${Math.max(1, m)} min`;
};
export const validTime = (t: string) => /^(\d{1,2}:)?[0-5]?\d:[0-5]\d$/.test(t.trim());
const DONE = 0.97;
export const isDone = (w: number) => w >= DONE;

/* ─── Shared: prototype store ─── */

type Library = { classes: ClassVideo[]; watched: Record<string, number>; fresh: string[] };
let lib: Library = { classes: CLASSES, watched: Object.fromEntries(CLASSES.map(c => [c.id, c.watched ?? 0])), fresh: [] };
const subs = new Set<() => void>();
const commit = (next: Library) => { lib = next; subs.forEach(f => f()); };

export function useLibrary() {
  return useSyncExternalStore(cb => { subs.add(cb); return () => { subs.delete(cb); }; }, () => lib);
}
export const library = {
  /** Remember the furthest point watched (0–1). */
  progress(id: string, w: number) {
    const was = lib.watched[id] ?? 0, now = w >= DONE ? 1 : w;
    if (now > was + 0.002) commit({ ...lib, watched: { ...lib.watched, [id]: now } });
  },
  setWatched(id: string, w: number) { commit({ ...lib, watched: { ...lib.watched, [id]: w } }); },
  save(c: ClassVideo, isNew: boolean) {
    commit(isNew
      ? { ...lib, classes: [c, ...lib.classes], watched: { ...lib.watched, [c.id]: 0 }, fresh: [...lib.fresh, c.id] }
      : { ...lib, classes: lib.classes.map(x => (x.id === c.id ? c : x)) });
  },
};

const when = (c: ClassVideo) => new Date(c.date).getTime() || 0;
/** A project's classes, oldest first (class 1, class 2…). */
export const projectClasses = (all: ClassVideo[], projectId: string) => all.filter(c => c.projectId === projectId).sort((a, b) => when(a) - when(b));
/** "Class 1 of 2", worked out from the project so uploads renumber themselves. */
export const partOf = (all: ClassVideo[], c: ClassVideo) => {
  const list = projectClasses(all, c.projectId);
  return list.length === 1 ? "Single class" : `Class ${list.findIndex(x => x.id === c.id) + 1} of ${list.length}`;
};
const monthStart = (p: Project) => new Date(p.month.replace(" ", " 1, ")).getTime() || 0;
export const projectById = (id: string) => PROJECTS.find(p => p.id === id);
/** Invented "seen by" count for Chelsea's view (prototype only; would come from analytics).
    Older projects and earlier classes have more views; a new upload starts at zero. */
export const seenBy = (all: ClassVideo[], c: ClassVideo, fresh: string[] = []) => {
  if (fresh.includes(c.id)) return 0;
  const p = PROJECTS.findIndex(x => x.id === c.projectId), i = projectClasses(all, c.projectId).findIndex(x => x.id === c.id);
  return 168 + Math.max(0, p) * 22 - i * 19 + (c.title.length % 9);
};

/* ─── Shared: air times ─── */

/** The current time, re-read every 30 seconds so "airs tonight" flips to "on air" and then to a replay. */
export function useNow(ms = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), ms); return () => window.clearInterval(t); }, [ms]);
  return now;
}
/** True when a class can be watched: it has aired (hasReplay) or Chelsea uploaded it this session. */
export const isOut = (c: ClassVideo, fresh: string[] = [], now = new Date()) => fresh.includes(c.id) || hasReplay(c, now);
const DAY: Intl.DateTimeFormatOptions = { year: "numeric", month: "numeric", day: "numeric" };
/** "tonight", "tomorrow" or "Thu, Oct 15" for a class's air date. */
export function airDay(c: ClassVideo, now = new Date()) {
  const s = classStart(c);
  if (datePart(s, DAY) === datePart(now, DAY)) return "tonight";
  if (datePart(s, DAY) === datePart(new Date(now.getTime() + 864e5), DAY)) return "tomorrow";
  return datePart(s, { weekday: "short", month: "short", day: "numeric" });
}
/** One line for a class that hasn't aired: "Airs tonight · replay right after", "Airs Thu, Oct 15 · 7 PM MT", "On air now". */
export function airLine(c: ClassVideo, now = new Date(), live = classStatus(c, now) === "live") {
  if (live) return "On air now · replay right after";
  const d = airDay(c, now);
  return d === "tonight" ? "Airs tonight · replay right after" : `Airs ${d} · 7 PM MT`;
}

/* ─── Shared: small pieces ─── */

const SKILLS = ["Beginner", "Confident beginner", "Intermediate"] as const;
export function Skill({ level }: { level: Project["skill"] }) {
  const n = SKILLS.indexOf(level) + 1;
  return <span className="mc-skill">
    <span className="mc-skill-dots" aria-hidden="true">{[1, 2, 3].map(i => <i key={i} className={i <= n ? "is-on" : ""} />)}</span>
    {level}
  </span>;
}

export function Progress({ w, label }: { w: number; label?: string }) {
  const pct = Math.round(w * 100);
  return <span className="mc-progress">
    <span className="mc-progress-bar" aria-hidden="true"><i style={{ width: `${Math.max(pct, w > 0 ? 3 : 0)}%` }} /></span>
    <span className="mc-progress-txt">{label ?? (isDone(w) ? "Watched" : w > 0 ? `${pct}% watched` : "Not started")}</span>
  </span>;
}

/** A transient message for prototype-only actions. */
export function useToast() {
  const [msg, setMsg] = useState<ReactNode>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const show = (m: ReactNode) => { setMsg(m); window.clearTimeout(timer.current); timer.current = window.setTimeout(() => setMsg(null), 5200); };
  const node = <div className="mc-toast-region" role="status" aria-live="polite">
    {msg && <div className="mc-toast"><span>{msg}</span><button type="button" onClick={() => setMsg(null)} aria-label="Dismiss"><X size={16} aria-hidden="true" /></button></div>}
  </div>;
  return [node, show] as const;
}

/* ─── Shared: upload / edit dialog (Chelsea) ─── */

type Row = { key: number; t: string; label: string };
const todayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: DENVER });
const isoOf = (date: string) => { const d = new Date(date); return Number.isNaN(d.getTime()) ? todayISO() : d.toLocaleDateString("en-CA"); };
const prettyDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const OBS_LENGTH = "1:31:52";

export function ClassDialog({ editing, onClose, onSaved }: { editing: ClassVideo | null; onClose: () => void; onSaved: (c: ClassVideo, isNew: boolean, notify: boolean) => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const keyN = useRef(0);
  const row = (t = "", label = ""): Row => ({ key: keyN.current++, t, label });
  const isNew = !editing;
  const [title, setTitle] = useState(editing?.title ?? "");
  const [projectId, setProjectId] = useState(editing?.projectId ?? PROJECTS[0].id);
  const [date, setDate] = useState(editing ? isoOf(editing.date) : todayISO());
  const [source, setSource] = useState<"obs" | "file">("obs");
  const [fileName, setFileName] = useState("");
  const [length, setLength] = useState(editing?.length ?? OBS_LENGTH);
  const [rows, setRows] = useState<Row[]>(() => editing ? editing.chapters.map(c => row(c.t, c.label)) : [row("0:00", "Hello + catch-up"), row("", "")]);
  const [notify, setNotify] = useState(true);
  const [tried, setTried] = useState(false);
  const all = useLibrary().classes;

  useEffect(() => { const d = ref.current; if (d && !d.open) d.showModal(); }, []);
  const close = () => ref.current?.close();

  const rowErr = (r: Row, i: number) => {
    if (!validTime(r.t)) return "Use m:ss or h:mm:ss";
    if (i === 0 && toSec(r.t) !== 0) return "The first chapter starts at 0:00";
    if (i > 0 && validTime(rows[i - 1].t) && toSec(r.t) <= toSec(rows[i - 1].t)) return "Times go in order";
    if (toSec(r.t) >= toSec(length || "0:00") && validTime(length)) return "Past the end of the video";
    if (!r.label.trim()) return "Give it a name";
    return "";
  };
  const errors = {
    title: title.trim() ? "" : "Add a title",
    length: validTime(length) ? "" : "Use h:mm:ss",
    file: source === "file" && isNew && !fileName ? "Choose a video file" : "",
    rows: rows.map(rowErr),
    none: rows.length ? "" : "Add at least one chapter",
  };
  const bad = !!(errors.title || errors.length || errors.file || errors.none || errors.rows.some(Boolean));
  const show = (e: string) => (tried ? e : "");
  const n = projectClasses(all.filter(c => c.id !== editing?.id), projectId).length;

  function submit(e: FormEvent) {
    e.preventDefault();
    setTried(true);
    if (bad) { requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>("[aria-invalid='true']")?.focus()); return; }
    const c: ClassVideo = {
      id: editing?.id ?? `${projectId}-${Date.now().toString(36)}`, projectId, title: title.trim(), part: "", date: prettyDate(date), length: length.trim(),
      poster: editing?.poster ?? "/video/live-class.jpg", chapters: rows.map(r => ({ t: r.t.trim(), label: r.label.trim() })),
    };
    onSaved(c, isNew, notify);
    close();
  }
  const move = (i: number, d: number) => setRows(rs => { const out = [...rs]; [out[i], out[i + d]] = [out[i + d], out[i]]; return out; });
  const edit = (i: number, patch: Partial<Row>) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return <dialog ref={ref} className="mc-dialog" aria-labelledby="mc-dialog-title" onClose={onClose}
    onClick={e => { if (e.target === e.currentTarget) close(); }}>
    <form className="mc-dialog-in" onSubmit={submit} noValidate>
      <header className="mc-dialog-head">
        <p className="mc-dialog-kicker">{isNew ? "New replay" : "Edit replay"}</p>
        <h2 id="mc-dialog-title">{isNew ? "Upload a class replay" : "Edit this class"}</h2>
        <button type="button" className="mc-dialog-x" onClick={close} aria-label="Close"><X size={20} aria-hidden="true" /></button>
      </header>

      <div className="mc-dialog-body">
        <label className="mc-field">
          <span>Title</span>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Straps, lining & finishing" aria-invalid={!!show(errors.title)} aria-describedby="mc-e-title" />
          <small id="mc-e-title" className="mc-err">{show(errors.title)}</small>
        </label>
        <div className="mc-field-pair">
          <label className="mc-field">
            <span>Project</span>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}>
              {PROJECTS.map(p => <option key={p.id} value={p.id}>{p.title} ({p.month})</option>)}
            </select>
            <small className="mc-hint">{isNew ? `Becomes class ${n + 1} of ${n + 1}` : "Class numbers follow the dates"}</small>
          </label>
          <label className="mc-field">
            <span>Class date</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
        </div>

        {isNew && <fieldset className="mc-field mc-source">
          <legend>Video</legend>
          <label className={`mc-source-opt${source === "obs" ? " is-on" : ""}`}>
            <input type="radio" name="mc-source" checked={source === "obs"} onChange={() => { setSource("obs"); setLength(OBS_LENGTH); }} />
            <Radio size={18} aria-hidden="true" />
            <span><b>Last club live</b><small>Recorded from OBS · club-live-{todayISO()}.mp4 · {OBS_LENGTH}</small></span>
          </label>
          <label className={`mc-source-opt${source === "file" ? " is-on" : ""}`}>
            <input type="radio" name="mc-source" checked={source === "file"} onChange={() => setSource("file")} />
            <Upload size={18} aria-hidden="true" />
            <span><b>Upload a file</b><small>{fileName || "MP4 or MOV, any length"}</small></span>
          </label>
          {source === "file" && <label className="mc-file">
            <span className="mb-sr">Video file</span>
            <input type="file" accept="video/*" onChange={e => setFileName(e.target.files?.[0]?.name ?? "")} aria-invalid={!!show(errors.file)} aria-describedby="mc-e-file" />
            <small id="mc-e-file" className="mc-err">{show(errors.file)}</small>
          </label>}
        </fieldset>}

        <label className="mc-field mc-field-short">
          <span>Length</span>
          <input value={length} onChange={e => setLength(e.target.value)} inputMode="numeric" placeholder="1:30:00" aria-invalid={!!show(errors.length)} aria-describedby="mc-e-length" />
          <small id="mc-e-length" className="mc-err">{show(errors.length)}</small>
        </label>

        <fieldset className="mc-field mc-chapters-edit">
          <legend>Chapters <small>Members tap these to jump around the replay.</small></legend>
          <ol>
            {rows.map((r, i) => { const err = show(errors.rows[i]); return <li key={r.key} className={err ? "has-err" : ""}>
              <span className="mc-ch-n" aria-hidden="true">{i + 1}</span>
              <input className="mc-ch-t" value={r.t} onChange={e => edit(i, { t: e.target.value })} placeholder="0:00" inputMode="numeric"
                aria-label={`Chapter ${i + 1} start time`} aria-invalid={!!err && !validTime(r.t)} aria-describedby={`mc-e-ch${r.key}`} />
              <input className="mc-ch-l" value={r.label} onChange={e => edit(i, { label: e.target.value })} placeholder="What happens here"
                aria-label={`Chapter ${i + 1} name`} aria-invalid={!!err && validTime(r.t) && !r.label.trim()} aria-describedby={`mc-e-ch${r.key}`} />
              <span className="mc-ch-tools">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move chapter ${i + 1} up`}><ArrowUp size={15} aria-hidden="true" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} aria-label={`Move chapter ${i + 1} down`}><ArrowDown size={15} aria-hidden="true" /></button>
                <button type="button" onClick={() => setRows(rs => rs.filter((_, j) => j !== i))} aria-label={`Remove chapter ${i + 1}`}><Trash2 size={15} aria-hidden="true" /></button>
              </span>
              <small id={`mc-e-ch${r.key}`} className="mc-err">{err}</small>
            </li>; })}
          </ol>
          {show(errors.none) && <small className="mc-err">{errors.none}</small>}
          <button type="button" className="mc-add-row" onClick={() => setRows(rs => [...rs, row()])}><Plus size={16} aria-hidden="true" /> Add a chapter</button>
        </fieldset>

        {isNew && <label className="mc-check">
          <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} />
          <span>Post “Replay is up” to the club feed and email members</span>
        </label>}
      </div>

      <footer className="mc-dialog-foot">
        <p className="mc-dialog-note">Prototype: nothing is uploaded or sent.</p>
        <button type="button" className="mb-btn mb-btn-line" onClick={close}>Cancel</button>
        <button type="submit" className="mb-btn mb-btn-ink">{isNew ? <><Upload size={16} aria-hidden="true" /> Publish replay</> : <><Check size={16} aria-hidden="true" /> Save changes</>}</button>
      </footer>
    </form>
  </dialog>;
}

/* ─── Header (gold) ─── */

function NextClass() {
  const to = useTo();
  const live = upcomingLives(1, "club")[0];
  if (!live) return null;
  const project = PROJECTS.find(p => p.month === datePart(live.start, { month: "long", year: "numeric" }));
  const tonight = datePart(live.start, DAY) === datePart(new Date(), DAY);
  const part = datePart(live.start, { weekday: "short" }) === "Thu" ? 2 : 1;
  return <motion.div className="mc-next" initial={{ opacity: 0, y: 30, rotate: 7 }} animate={{ opacity: 1, y: 0, rotate: 2.5 }} transition={{ delay: 0.35, type: "spring", stiffness: 130, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
    <p className="mc-next-label">{tonight ? "Live tonight" : "Next live class"}</p>
    <div className="mc-next-row">
      <span className="mc-next-cal" aria-hidden="true">
        <small>{datePart(live.start, { weekday: "short" })}</small>
        <b>{datePart(live.start, { day: "numeric" })}</b>
        <span>{datePart(live.start, { month: "short" })}</span>
      </span>
      <div>
        <p className="mc-next-title">{project ? `${project.title}, class ${part}` : "Club class"}</p>
        <p className="mc-next-when">{datePart(live.start, { weekday: "long", month: "long", day: "numeric" })} · 7 PM MT</p>
      </div>
    </div>
    <p className="mc-next-note">The replay lands here the same night, chapters and all.</p>
    <Link to={to(M.live)} className="mc-next-link">Go to the live room <ArrowRight size={15} aria-hidden="true" /></Link>
  </motion.div>;
}

/** What membership adds, for visitors and Thread Friends (the lock itself is in the library below). */
const PERKS = ["Every replay, with chapters", "Supply lists & patterns", "Two live classes a month", "Ask Chelsea about any class"];
function PerksNote() {
  const role = useRole();
  return <motion.div className="mc-perks" initial={{ opacity: 0, y: 30, rotate: -7 }} animate={{ opacity: 1, y: 0, rotate: -2.5 }} transition={{ delay: 0.35, type: "spring", stiffness: 130, damping: 15 }}>
    <i className="b2-snap-tape b2-ktape-extra" aria-hidden="true" />
    <p className="mc-perks-hand">{role === "friend" ? "hi, Thread Friend!" : "what members get"}</p>
    <p className="mc-perks-text">{role === "friend"
      ? <>Your {PLANS.friend.name} plan covers posts and studio updates. <b>{PLANS.member.name}, {PLANS.member.per}</b>, adds:</>
      : <>Every title is out on the shelf for you to browse. <b>{PLANS.member.name}s, {PLANS.member.per}</b>, also get:</>}</p>
    <ul className="mc-perks-list" aria-label="Members get">
      {PERKS.map(t => <li key={t}><Check size={15} strokeWidth={3} aria-hidden="true" />{t}</li>)}
    </ul>
  </motion.div>;
}

function Header({ onUpload }: { onUpload: () => void }) {
  const role = useRole();
  const to = useTo();
  const now = useNow();
  const { classes, watched, fresh } = useLibrary();
  const open = can(role, "classes"), chelsea = role === "chelsea";
  // Only classes that have aired (or that Chelsea just uploaded) count as replays.
  const out = classes.filter(c => isOut(c, fresh, now));
  const total = out.reduce((s, c) => s + toSec(c.length), 0);
  const projects = new Set(classes.map(c => c.projectId)).size;
  const resume = role === "member" ? [...out].sort(byNewest).find(c => (watched[c.id] ?? 0) > 0 && !isDone(watched[c.id])) : undefined;
  const word = "library";
  return <div className="mc-head">
    <div className="mc-head-copy">
      <motion.p className="mc-kicker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>Loose Threads Club · Classes</motion.p>
      <h1 className="mc-title" aria-label="The class library">
        <motion.span className="mc-title-hand" aria-hidden="true" initial={{ opacity: 0, rotate: -12 }} animate={{ opacity: 1, rotate: -5 }} transition={{ delay: 0.35, duration: 0.5, ease: EASE }}>every class in the</motion.span>
        <span className="mc-title-word" aria-hidden="true">
          {word.split("").map((ch, i) => <motion.span key={i} initial={{ y: "18%", opacity: 0 }} animate={{ y: "0%", opacity: 1 }} transition={{ delay: 0.1 + i * 0.05, duration: 0.6, ease: EASE }}>{ch}</motion.span>)}
        </span>
      </h1>
      <motion.p className="mc-lede" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.45, ease: EASE }}>
        Every live class, recorded and chaptered so you can jump straight to the zipper part. Sew along at your own pace, pause as much as you like.
      </motion.p>
      <motion.ul className="mc-stats" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.45, ease: EASE }}>
        <li><b>{out.length}</b> {out.length === 1 ? "replay" : "replays"}</li>
        <li><b>{projects}</b> projects</li>
        <li><b>{human(total)}</b> of sewing</li>
      </motion.ul>
      <motion.div className="mc-head-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.45, ease: EASE }}>
        {chelsea && <><button type="button" className="mb-btn mb-btn-hero" onClick={onUpload}><Upload size={17} aria-hidden="true" /> Upload replay</button>
          <Link to={to(M.live)} className="mb-btn mb-btn-line"><Radio size={17} aria-hidden="true" /> Go live</Link></>}
        {resume && <><Link to={to(M.klass(resume.id))} className="mb-btn mb-btn-hero"><Play size={16} fill="currentColor" aria-hidden="true" /> Keep watching</Link>
          <a href="#mc-all" className="mb-btn mb-btn-line">Browse classes</a></>}
        {role === "member" && !resume && <a href="#mc-all" className="mb-btn mb-btn-hero">Browse classes <ArrowRight size={16} aria-hidden="true" /></a>}
        {!open && <><JoinButton need="members" variant="hero" />
          <a href="#mc-all" className="mb-btn mb-btn-line">See what’s inside</a></>}
      </motion.div>
    </div>
    <div className="mc-head-side">{open ? <NextClass /> : <PerksNote />}</div>
  </div>;
}

/* ─── Library ─── */

type Match = { c: ClassVideo; chapter?: { t: string; label: string } };

function ClassCard({ m, i, now, onEdit }: { m: Match; i: number; now: Date; onEdit: (c: ClassVideo) => void }) {
  const role = useRole();
  const to = useTo();
  const { classes, watched, fresh } = useLibrary();
  const { c, chapter } = m;
  const seen = seenBy(classes, c, fresh);
  const out = isOut(c, fresh, now), live = !out && classStatus(c, now) === "live";
  const open = can(role, "classes"), w = out ? watched[c.id] ?? 0 : 0;
  const part = partOf(classes, c);
  const href = to(open && out && chapter ? `${M.klass(c.id)}?t=${toSec(chapter.t)}` : M.klass(c.id));
  const when = out ? human(toSec(c.length)) : airLine(c, now, live);
  return <motion.li className={`mc-card${out ? "" : " is-soon"}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: (i % 3) * 0.08, duration: 0.5, ease: EASE }}>
    <Link to={href} className="mc-card-link" aria-label={`${part}: ${c.title}, ${when}${open ? "" : " (members only)"}`}>
      <span className="mc-poster">
        <img src={c.poster} alt="" loading="lazy" />
        {out
          ? <><span className="mc-len">{c.length}</span>
            <b className={`mc-play${open ? "" : " is-locked"}`} aria-hidden="true">{open ? <Play size={18} fill="currentColor" /> : <Lock size={17} />}</b></>
          : <span className={`mc-airs${live ? " is-live" : ""}`} aria-hidden="true">
            {live ? <Radio size={15} /> : <CalendarDays size={15} />}{live ? "on air now" : `airs ${airDay(c, now)}`}
          </span>}
        {open && role === "member" && isDone(w) && <span className="mc-done"><Check size={13} strokeWidth={3} aria-hidden="true" /> watched</span>}
        {fresh.includes(c.id) && <span className="mc-new">new!</span>}
        {open && role === "member" && w > 0 && !isDone(w) && <span className="mc-poster-bar" aria-hidden="true"><i style={{ width: `${w * 100}%` }} /></span>}
      </span>
      <span className="mc-card-body">
        <span className="mc-part">{part}</span>
        <span className="mc-card-title">{c.title}</span>
        {out ? <span className="mc-meta">{c.date} · {c.chapters.length} chapters</span>
          : <span className="mc-meta mc-soon-line"><b>{when.split(" · ")[0]}</b> · {when.split(" · ")[1]}</span>}
        {chapter && <span className="mc-match">{!out ? "Covers" : open ? "Jumps to" : "Includes"} “{chapter.label}”{out ? ` · ${chapter.t}` : ""}</span>}
        {role === "member" && out && <Progress w={w} />}
        {role === "chelsea" && <span className="mc-meta mc-seen">{!out ? "Not aired yet" : seen ? `Seen by ${seen} members` : "Just published"}</span>}
        {!open && <VisBadge visibility="members" size="sm" className="mc-vis" />}
      </span>
    </Link>
    {role === "chelsea" && <button type="button" className="mc-edit" onClick={() => onEdit(c)} aria-label={`Edit ${c.title}`}><Pencil size={14} aria-hidden="true" /><span className="mc-edit-txt">Edit</span></button>}
  </motion.li>;
}

function ProjectRow({ p, matches, now, onEdit }: { p: Project; matches: Match[]; now: Date; onEdit: (c: ClassVideo) => void }) {
  const { classes, fresh } = useLibrary();
  const all = projectClasses(classes, p.id);
  const aired = all.filter(c => isOut(c, fresh, now));
  const soon = all.find(c => !isOut(c, fresh, now));
  const len = aired.reduce((s, c) => s + toSec(c.length), 0);
  return <section className="mc-proj" aria-labelledby={`mc-p-${p.id}`}>
    <motion.figure className="mc-proj-snap" initial={{ opacity: 0, rotate: 6, y: 20 }} whileInView={{ opacity: 1, rotate: -3, y: 0 }} viewport={VIEW} transition={{ type: "spring", stiffness: 120, damping: 15 }}>
      <i className="b2-snap-tape b2-ktape-pop" aria-hidden="true" />
      <img src={p.image} alt={`The ${p.title} project`} loading="lazy" />
      <figcaption>{p.month.split(" ")[0].toLowerCase()} ’{p.month.slice(-2)}</figcaption>
    </motion.figure>
    <div className="mc-proj-main">
      <div className="mc-proj-top">
        <p className="mc-proj-month">{p.month}</p>
        <h3 id={`mc-p-${p.id}`} className="mc-proj-title">{p.title}</h3>
        <Skill level={p.skill} />
      </div>
      <div className="mc-proj-more">
        <p className="mc-proj-blurb">{p.blurb}</p>
        <ul className="mc-proj-facts">
          <li>{all.length} {all.length === 1 ? "class" : "classes"}{aired.length ? ` · ${human(len)}${soon ? " so far" : ""}` : ""}</li>
          {soon && <li className="mc-fact-soon">{partOf(classes, soon).replace(/ of \d+$/, "")} {airLine(soon, now).split(" · ")[0].toLowerCase()}</li>}
          <li>{p.supplies.length} supplies</li>
          {p.pattern && <li>Pattern included</li>}
        </ul>
      </div>
      <ul className="mc-cards">
        {matches.map((m, i) => <ClassCard key={m.c.id} m={m} i={i} now={now} onEdit={onEdit} />)}
        <motion.li className="mc-note" initial={{ opacity: 0, rotate: 8 }} whileInView={{ opacity: 1, rotate: 2 }} viewport={VIEW} transition={{ delay: 0.25, type: "spring", stiffness: 120, damping: 14 }}>
          <i className="b2-snap-tape b2-ktape-soft" aria-hidden="true" />
          <p className="mc-note-hand">you’ll need…</p>
          <ul>{p.supplies.slice(0, 4).map(s => <li key={s}>{s}</li>)}{p.supplies.length > 4 && <li>+ {p.supplies.length - 4} more</li>}</ul>
          {p.pattern && <p className="mc-note-foot">pattern included ✓</p>}
        </motion.li>
      </ul>
    </div>
  </section>;
}

function ContinueWatching({ now }: { now: Date }) {
  const role = useRole();
  const to = useTo();
  const { classes, watched, fresh } = useLibrary();
  if (role !== "member") return null;
  const list = classes.filter(c => { const w = watched[c.id] ?? 0; return isOut(c, fresh, now) && w > 0 && !isDone(w); }).sort(byNewest);
  if (!list.length) return null;
  return <section className="mc-continue" aria-labelledby="mc-continue-title">
    <Head id="mc-continue-title" hand="pick up where" word="you left off" tone="blue" className="mb-headsm" />
    <ul className="mc-continue-list">
      {list.map((c, i) => {
        const w = watched[c.id] ?? 0, len = toSec(c.length), at = Math.floor(w * len), p = projectById(c.projectId);
        return <motion.li key={c.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={VIEW} transition={{ delay: i * 0.1, duration: 0.5, ease: EASE }}>
          <Link to={to(`${M.klass(c.id)}?t=${at}`)} className="mc-cw">
            <span className="mc-cw-poster">
              <img src={c.poster} alt="" loading="lazy" />
              <b aria-hidden="true"><Play size={18} fill="currentColor" /></b>
              <span className="mc-poster-bar" aria-hidden="true"><i style={{ width: `${w * 100}%` }} /></span>
            </span>
            <span className="mc-cw-copy">
              <span className="mc-part">{p?.title} · {partOf(classes, c)}</span>
              <span className="mc-cw-title">{c.title}</span>
              <span className="mc-cw-left">{human(len - at)} left</span>
              <span className="mc-cw-go">Resume at {clock(at)} <ArrowRight size={15} aria-hidden="true" /></span>
            </span>
          </Link>
        </motion.li>;
      })}
    </ul>
  </section>;
}

const FILTERS = ["All", ...SKILLS] as const;
type Filter = (typeof FILTERS)[number];

function Library({ onEdit }: { onEdit: (c: ClassVideo) => void }) {
  const { classes, fresh } = useLibrary();
  const now = useNow();
  const [query, setQuery] = useState("");
  const [skill, setSkill] = useState<Filter>("All");
  const q = query.trim().toLowerCase();

  const groups = useMemo(() => {
    const has = (s: string) => s.toLowerCase().includes(q);
    return [...PROJECTS].sort((a, b) => monthStart(b) - monthStart(a))
      .filter(p => skill === "All" || p.skill === skill)
      .map(p => {
        const projectHit = !q || has(p.title) || has(p.blurb) || p.supplies.some(has);
        const matches: Match[] = [];
        for (const c of projectClasses(classes, p.id)) {
          const chapter = q ? c.chapters.find(ch => has(ch.label)) : undefined;
          if (projectHit || has(c.title) || chapter) matches.push({ c, chapter });
        }
        return { p, matches };
      })
      .filter(g => g.matches.length);
  }, [classes, q, skill]);
  const all = groups.flatMap(g => g.matches);
  const count = all.length, replayN = all.filter(m => isOut(m.c, fresh, now)).length, soonN = count - replayN;
  const reset = () => { setQuery(""); setSkill("All"); };
  const tally = [replayN && `${replayN} ${replayN === 1 ? "replay" : "replays"}`, soonN && `${soonN} coming up`].filter(Boolean).join(" and ");

  return <section id="mc-all" className="mc-lib" aria-labelledby="mc-lib-title">
    <div className="b2-wrap">
      <ContinueWatching now={now} />
      <Locked what="replays" hand="browse freely, watch as a member" className="mc-lock" />
      <div className="mc-lib-row">
        <Head id="mc-lib-title" hand="sew it" word="again" tone="blue" className="mb-headsm" />
        <svg className="mc-lib-heart" viewBox="0 0 30 46" aria-hidden="true"><Ink d={HEART} delay={0.5} width={3} /></svg>
      </div>
      <div className="mc-tools" role="search">
        <label className="mc-search">
          <Search size={18} aria-hidden="true" />
          <span className="mb-sr">Search classes</span>
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search classes, chapters, supplies…" />
          {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search"><X size={16} aria-hidden="true" /></button>}
        </label>
        <div className="mc-chips" role="group" aria-label="Skill level">
          {FILTERS.map(f => <button key={f} type="button" className={`mc-chip${skill === f ? " is-on" : ""}`} aria-pressed={skill === f} onClick={() => setSkill(f)}>{f === "All" ? "All levels" : f}</button>)}
        </div>
      </div>
      <p className="mc-count" aria-live="polite">
        {count === 0 ? "No classes match." : q || skill !== "All" ? `${tally} ${count === 1 ? "matches" : "match"}${q ? ` “${query.trim()}”` : ""}${skill !== "All" ? ` · ${skill}` : ""}` : `${tally}, newest project first`}
      </p>

      {groups.length ? groups.map(g => <ProjectRow key={g.p.id} p={g.p} matches={g.matches} now={now} onEdit={onEdit} />)
        : <div className="mc-empty">
          <svg viewBox="0 0 120 70" aria-hidden="true"><Ink d="M8 52 C 30 10, 54 64, 70 30 S 104 14, 112 40" width={3} /><Ink d="M96 18 l 10 -10 M100 26 l 14 -4" delay={0.4} width={3} /></svg>
          <p className="mc-empty-hand">nothing on the cutting table</p>
          <p>Try another word, or look through everything.</p>
          <button type="button" className="mb-btn mb-btn-ink" onClick={reset}>Show all classes</button>
        </div>}

      <aside className="mc-yt" aria-label="Free lives on YouTube">
        <span className="mc-yt-icon"><Youtube size={26} aria-hidden="true" /></span>
        <div>
          <p className="mc-yt-hand">between classes…</p>
          <p className="mc-yt-text">Most other Tuesdays I’m live on YouTube, free, at 7 PM MT. Come hang out and bring a project.</p>
        </div>
        <a href={YOUTUBE_LIVE_URL} target="_blank" rel="noreferrer" className="mb-btn mb-btn-line">Watch on YouTube <ArrowRight size={16} aria-hidden="true" /></a>
      </aside>
    </div>
  </section>;
}

type DialogState = { editing: ClassVideo | null } | null;

function Page({ dialog, setDialog }: { dialog: DialogState; setDialog: (d: DialogState) => void }) {
  const role = useRole();
  const [toast, showToast] = useToast();
  const saved = (c: ClassVideo, isNew: boolean, notify: boolean) => {
    library.save(c, isNew);
    showToast(<>{isNew ? <>Replay published{notify ? " and members notified" : ""}.</> : <>Saved “{c.title}”.</>} <Link to={withRole(M.klass(c.id), role)}>View class</Link></>);
  };
  return <>
    <Part p="club"><Library onEdit={c => setDialog({ editing: c })} /></Part>
    {dialog && role === "chelsea" && <Part p="club"><ClassDialog editing={dialog.editing} onClose={() => setDialog(null)} onSaved={saved} /></Part>}
    {toast}
  </>;
}

export function MembersClasses() {
  const [dialog, setDialog] = useState<DialogState>(null);
  return <MotionConfig reducedMotion="user">
    <MembersPage page="classes" header={<Header onUpload={() => setDialog({ editing: null })} />}>
      <Page dialog={dialog} setDialog={setDialog} />
    </MembersPage>
  </MotionConfig>;
}
